import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { FULL_DATE_WITH_DASHES } from '@travel/traveler-core/utils/dateTime';
import { isNotEmptyArray } from '@travel/utils';

import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

import type {
  ChildrenCategory,
  FeatureGroup,
  FeatureGroupManagementName,
  URLSearchQuery,
} from '../types/jrdpCommon';

dayjs.extend(customParseFormat);
type TimeDuration = {
  days: number;
  hours: number;
  minutes: number;
};

export enum URL_KEYS {
  ELEMENTARY_CHILDREN = 'childElementary',
  INFANT_BED_MEALS = 'infantBedMeal',
  INFANT = 'infantNone',
  NO_OF_UNITS = 'noOfUnits',
  ADULTS = 'adults',
  CHECK_IN_DATE = 'checkInDate',
  CHECK_OUT_DATE = 'checkOutDate',
  DEPARTURE_DATE = 'departureDate',
  RETURN_DATE = 'returnDate',
  DEPARTURE = 'departure',
  PLACE_OF_STAY = 'placeOfStay',
  ACCOMMODATION_AREA = 'accommodationArea',
  SORT = 'sort',
  FEATURES = 'features',
  MAX_PRICE_CAMPAIGN = 'maxPrice',
  MIN_PRICE_CAMPAIGN = 'minPrice',
  MIN_PRICE = 'minPricePerAdult',
  MAX_PRICE = 'maxPricePerAdult',

  // Map
  ZOOM_LEVEL = 'zoomLevel',
  LATITUDE = 'latitude',
  LONGITUDE = 'longitude',

  // train search
  TRANSACTION_ID = 'transactionId',
  DEPARTURE_STATION = 'baseDepartureStationCode',
  ARRIVAL_STATION = 'baseArrivalStationCode',
  PROVIDER_ID = 'providerId',
  ITEM_ID = 'itemId',
  PLAN_ID = 'ratePlanId',
  ROUTE_NO = 'routeNo',
}

export const DEFAULT_ROOM = 1;
export const DEFAULT_ADULT = 2;
export const DEFAULT_CHILDREN = 0;
export const DEFAULT_INFANT_BED_MEAL = 0;
export const DEFAULT_INFANT_NONE = 0;
export const DEFAULT_STAY_NIGHT = 1;
export const DEFAULT_TRAVEL_DAYS = 2;

export const MAX_ADULT_COUNT = 6;
export const MAX_ROOM_COUNT = 9;
export const MAX_STAY_NIGHT = 5;

const CHILDREN_KEYS: Array<{ urlKey: URL_KEYS; requestKey: ChildrenCategory }> = [
  {
    urlKey: URL_KEYS.ELEMENTARY_CHILDREN,
    requestKey: 'Elementary',
  },
  {
    urlKey: URL_KEYS.INFANT_BED_MEALS,
    requestKey: 'Infants_BedsMeals',
  },
  {
    urlKey: URL_KEYS.INFANT,
    requestKey: 'Infants_None',
  },
];

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);
dayjs.extend(utc);
dayjs.extend(timezone);

dayjs.tz.setDefault('Asia/Tokyo');
export const timezoneDayjs = (...args: any[]) => {
  return dayjs(...args).tz();
};

export const getValidDateRange = (selectedStartDate?: string, selectedEndDate?: string) => {
  const today = timezoneDayjs();
  const validSelectedStartDate = isDateValid(selectedStartDate);
  const validSelectedEndDate = isDateValid(selectedEndDate);

  let defaultStartDay = today.add(2, 'day');
  let defaultEndDay;

  const isOnOrAfter6PM = today.hour() >= 18;

  const startDay = timezoneDayjs(selectedStartDate || undefined);
  const endDay = timezoneDayjs(selectedEndDate || undefined);

  if (
    (!validSelectedStartDate && !validSelectedEndDate) ||
    startDay.isBefore(today, 'day') ||
    endDay.isBefore(today, 'day')
  ) {
    //If direct landing OR intentionally past values given in query strings: default to the standard +2 days
    defaultEndDay = defaultStartDay.add(1, 'day');
  } else if (!validSelectedStartDate && validSelectedEndDate) {
    defaultEndDay = endDay.isSameOrBefore(today, 'day')
      ? isOnOrAfter6PM
        ? today.add(2, 'day')
        : today.add(1, 'day')
      : endDay;
    defaultStartDay = defaultEndDay.subtract(1, 'day');
  } else {
    // if given start date or today (as a start date) is more than 6PM
    // then giving tmr as a expected start date
    defaultStartDay =
      isOnOrAfter6PM && startDay.isSameOrBefore(today, 'day')
        ? today.add(1, 'day')
        : startDay.isBefore(today, 'day')
        ? today
        : startDay;
    defaultEndDay =
      !validSelectedEndDate || endDay.isSameOrBefore(defaultStartDay, 'day')
        ? defaultStartDay.add(1, 'day')
        : endDay;
  }

  return {
    startDate: defaultStartDay.format(FULL_DATE_WITH_DASHES),
    endDate: defaultEndDay.format(FULL_DATE_WITH_DASHES),
  };
};

export const getValidStringArray = (data?: string | Array<string>) => {
  const list = Array.isArray(data) ? data : (data || '').split(',');
  return list.reduce((result: Array<string>, item) => {
    if (!item) return result;
    return [...result, item];
  }, []);
};

export const normalizeQueryParams = (places?: string | string[]) => {
  if (places && places[places.length - 1] === '/') {
    return places.slice(0, -1);
  }
  return places;
};

export const isDateValid = (
  date?: string,
  format: string = 'YYYY-MM-DD',
  strict: boolean = true,
) => {
  const parsedDate = dayjs(date, format, strict);
  return parsedDate.isValid();
};

export function getDefaultURLSearchRequest(query?: URLSearchQuery) {
  const { startDate: departureDate, endDate: returnDate } = getValidDateRange(
    query?.[URL_KEYS.DEPARTURE_DATE],
    query?.[URL_KEYS.RETURN_DATE],
  );

  const { startDate: checkInDate, endDate: checkOutDate } = getValidDateRange(
    query?.[URL_KEYS.CHECK_IN_DATE],
    query?.[URL_KEYS.CHECK_OUT_DATE],
  );

  const places = normalizeQueryParams(query?.[URL_KEYS.PLACE_OF_STAY]);

  return {
    startDate: departureDate,
    endDate: returnDate,
    checkInDate: checkInDate,
    checkOutDate: checkOutDate,

    departureArea: query?.[URL_KEYS.DEPARTURE],
    accommodations: getValidStringArray(places),
    accommodationArea: query?.[URL_KEYS.ACCOMMODATION_AREA] || '',

    adults: Number(query?.[URL_KEYS.ADULTS]) || DEFAULT_ADULT,
    rooms: Math.min(Number(query?.[URL_KEYS.NO_OF_UNITS]) || DEFAULT_ROOM, MAX_ROOM_COUNT),
    children: CHILDREN_KEYS.map(key => ({
      category: key.requestKey,
      count: Number(query?.[key.urlKey as keyof URLSearchQuery]) || 0,
    })),
  };
}

export const parseSearchQuery = (query?: URLSearchQuery) => {
  const numberOfChildren =
    Number(query?.[URL_KEYS.ELEMENTARY_CHILDREN]) +
      Number(query?.[URL_KEYS.INFANT]) +
      Number(query?.[URL_KEYS.INFANT_BED_MEALS]) || DEFAULT_CHILDREN;

  //For Checkin/checkout case, use this value - 1 if departure/return is specified, but checkin/out is not
  const numDaysBasedOnQuery =
    getTotalDuration(query?.[URL_KEYS.DEPARTURE_DATE] || '', query?.[URL_KEYS.RETURN_DATE] || '')
      .days + 1;
  return {
    adultCount: Number(query?.[URL_KEYS.ADULTS]) || DEFAULT_ADULT,
    childCount: numberOfChildren,
    noOfDays: numDaysBasedOnQuery || DEFAULT_TRAVEL_DAYS,
    noOfUnits: Math.min(Number(query?.[URL_KEYS.NO_OF_UNITS]) || DEFAULT_ROOM, MAX_ROOM_COUNT),
    stayNights:
      getTotalDuration(
        query?.[URL_KEYS.CHECK_IN_DATE] || '',
        query?.[URL_KEYS.CHECK_OUT_DATE] || '',
      ).days ||
      numDaysBasedOnQuery - 1 ||
      DEFAULT_STAY_NIGHT,
  };
};

export const getSearchRatParams = (query?: URLSearchQuery) => {
  const queryString = new URLSearchParams(query as Record<string, string>).toString();

  const { adultCount, childCount, noOfDays, stayNights } = parseSearchQuery(query);
  const {
    startDate,
    endDate,
    checkInDate,
    checkOutDate,
    departureArea,
    accommodationArea,
    rooms,
    children,
  } = getDefaultURLSearchRequest(query);

  const totalInfantCount = children?.reduce((acc, child) => {
    if (
      (child.count > 0 && child.category === 'Infants_None') ||
      child.category === 'Infants_BedsMeals'
    ) {
      return acc + child.count;
    }
    return acc;
  }, 0);

  return {
    query: queryString,
    numberOfAdults: adultCount,
    numberOfChildren: childCount,
    numberOfDays: noOfDays,
    stayNights: stayNights,
    departureDate: startDate,
    returnDate: endDate,
    departureArea,
    checkInDate,
    checkOutDate,
    destinationArea: accommodationArea,
    numberOfRooms: rooms,
    numberOfInfants: totalInfantCount,
  };
};

const featureManagementName: { [key: string]: FeatureGroupManagementName } = {
  TYPE: 'Room_Type',
  GRADE: 'Grade',
  LOCATION: 'Location',
  SMOKING_CONDITION: 'Smoking_Type',
  BATH_TOILET_CONDITION: 'Facility',
  FACILITY: 'Facility',
  VIEW: 'View',
  OPEN_AIR_BATH: 'Open_Air_Bath',
  AMENITY: 'Amenity',
  SERVICE: 'Service',
  MEAL: 'Meal',
  PAYMENT_METHOD: 'Payment_Method',
  SPECIAL_PROVIDER_CATEGORY: 'Special_Provider_Category',
};

const bathAndToiletConditions = [
  'in_room_bathroom_toilet',
  'in_room_shower_toilet',
  'no_bathroom_with_toilet',
  'in_room_bathroom_no_toilet',
  'shower_only',
  'no_bathroom_toilet',
];

export const getBathToiletConditionFeature = (featureGroups: FeatureGroup[]) => {
  const filteredArray = featureGroups.reduce((result: FeatureGroup[], item) => {
    if (item.groupManagementName === featureManagementName.BATH_TOILET_CONDITION) {
      const toiletFeatures = item.features.filter(feature =>
        bathAndToiletConditions.includes(`${feature.managementName}`),
      );

      if (toiletFeatures.length > 0) {
        result.push({
          groupManagementName: item.groupManagementName,
          features: toiletFeatures.slice(0, 1), // Get only one feature
        });
      }
    }
    return result;
  }, []);

  return filteredArray;
};

export const findFeaturesByName = (groupName: string, featureGroups?: FeatureGroup[]) => {
  const featureGroup = featureGroups?.find(
    featureGroup => featureGroup.groupManagementName === featureManagementName[groupName],
  );

  return featureGroup?.features || [];
};

export const getItemFeatures = (featureGroups: FeatureGroup[]) => {
  const typeFeatures = findFeaturesByName('TYPE', featureGroups);
  // 	When there's setting
  const gradeFeatures = findFeaturesByName('GRADE', featureGroups);
  // 	When there's setting
  const locationFeatures = findFeaturesByName('LOCATION', featureGroups);
  const smokingConditionFeatures = findFeaturesByName('SMOKING_CONDITION', featureGroups);

  // 	When there's setting
  //TODO remove after Itinerary page integration of ItemFeatures
  const bathToiletConditionFeatures = findFeaturesByName('BATH_TOILET_CONDITION', featureGroups);
  const facilityFeatures = findFeaturesByName('BATH_TOILET_CONDITION', featureGroups);

  const viewFeatures = findFeaturesByName('VIEW', featureGroups);

  const openAirBathFeatures = findFeaturesByName('OPEN_AIR_BATH', featureGroups);

  const amenityFeatures = findFeaturesByName('AMENITY', featureGroups);
  const mealsIncluded = findFeaturesByName('MEAL', featureGroups);
  const paymentMethod = findFeaturesByName('PAYMENT_METHOD', featureGroups);

  return {
    typeFeatures,
    bathToiletConditionFeatures,
    facilityFeatures,
    smokingConditionFeatures,
    locationFeatures,
    gradeFeatures,
    openAirBathFeatures,
    viewFeatures,
    amenityFeatures,
    mealsIncluded,
    paymentMethod,
  };
};

export const getFeatures = (featureGroups = [] as FeatureGroup[], name: string) =>
  featureGroups.find(featureGroup => featureGroup.groupManagementName === name)?.features || [];

export function generateDataLayerStringData<T>(originalData: Array<T>, key: keyof T): string {
  return isNotEmptyArray(originalData) ? originalData.map(data => data[key])?.join(',') : '';
}

export const setDateWithCustomTime = (time: string) => {
  const [hour, minute] = time.split(':');
  return timezoneDayjs(`2000-01-01`)
    .set('hour', parseInt(hour))
    .set('minute', parseInt(minute));
};

export const stringifyQueryParameter = (query?: URLSearchQuery) =>
  new URLSearchParams(query as Record<string, string>).toString();

export const getTimeDifference = (timeA: string, timeB: string) => {
  return getTotalDuration(
    setDateWithCustomTime(timeA).format('YYYY-MM-DD HH:mm:ss'),
    setDateWithCustomTime(timeB).format('YYYY-MM-DD HH:mm:ss'),
  );
};

export const getTotalDuration = (dateA: string, dateB: string) => {
  const timeDuration = dayjs.duration(timezoneDayjs(dateB).diff(dateA));
  return {
    days: timeDuration.get('days'),
    hours: timeDuration.get('hours'),
    minutes: timeDuration.get('minutes'),
  } as TimeDuration;
};

export const getComparedTime = (timeA: string, timeB: string, isBefore: boolean) => {
  if (isBefore) {
    return setDateWithCustomTime(timeA).isSameOrBefore(setDateWithCustomTime(timeB))
      ? timeA
      : timeB;
  } else {
    return setDateWithCustomTime(timeA).isAfter(setDateWithCustomTime(timeB)) ? timeA : timeB;
  }
};

export const addHour = (time: string, nbHour: number) => {
  const date = setDateWithCustomTime(time);
  return date.add(nbHour, 'hours');
};

export const addMins = (time: string, nbMins: number) => {
  const date = setDateWithCustomTime(time);
  return date.add(nbMins, 'minute');
};

export const getDate = (date: string) => timezoneDayjs(date).format('YYYY-MM-DD');
export const getTime = (date: string) => timezoneDayjs(date).format('HH:mm');
